/**
 * 元素观察者管理器，统一管理 ResizeObserver、MutationObserver 和 IntersectionObserver
 * Window resize 事件已迁移到 subjects.ts 统一管理
 */

import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubjectKeys, subjectManager } from './subjects';

interface ResizeEvent {
  element: HTMLElement;
  entry: ResizeObserverEntry;
}

interface MutationEvent {
  element: HTMLElement;
}

interface IntersectionEvent {
  element: HTMLElement;
  isIntersecting: boolean;
  entry: IntersectionObserverEntry;
}

interface CleanupFunction {
  (): void;
}

class ElementObserverManager {
  private resizeSubject: Subject<ResizeEvent> | undefined;
  private resizeObserver: ResizeObserver | null = null;
  private observedElements = new WeakSet<HTMLElement>();

  private mutationSubject: Subject<MutationEvent> | undefined;
  private mutationObserver: MutationObserver | null = null;
  private mutatedElements = new WeakSet<HTMLElement>();

  private intersectionSubject: Subject<IntersectionEvent> | undefined;
  private intersectionObserver: IntersectionObserver | null = null;
  private intersectedElements = new WeakSet<HTMLElement>();

  private cleanupFunctions = new Map<'resize' | 'mutation' | 'intersection', CleanupFunction>();

  getResizeSubject(): Subject<ResizeEvent> | undefined {
    try {
      if (!this.resizeSubject) {
        this.createResizeSubject();
      }
      return this.resizeSubject;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get resize subject:', error);
      return undefined;
    }
  }

  getMutationSubject(): Subject<MutationEvent> | undefined {
    try {
      if (!this.mutationSubject) {
        this.createMutationSubject();
      }
      return this.mutationSubject;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get mutation subject:', error);
      return undefined;
    }
  }

  observeResize(element: HTMLElement, callback: () => void): () => void {
    const subject = this.getResizeSubject();
    if (!subject) {
      return () => {};
    }

    if (!this.observedElements.has(element)) {
      this.observedElements.add(element);
      if (this.resizeObserver) {
        this.resizeObserver.observe(element);
      }
    }

    const subscription = subject.pipe(filter(event => event.element === element)).subscribe(() => {
      try {
        callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in resize callback:', error);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }

  observeMutation(element: HTMLElement, callback: () => void): () => void {
    const subject = this.getMutationSubject();
    if (!subject) {
      return () => {};
    }

    if (!this.mutatedElements.has(element)) {
      this.mutatedElements.add(element);
      if (this.mutationObserver) {
        this.mutationObserver.observe(element, {
          childList: true,
          characterData: true,
          subtree: false,
        });
      }
    }

    const subscription = subject.pipe(filter(event => event.element === element)).subscribe(() => {
      try {
        callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in mutation callback:', error);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }

  observeWindowResize(callback: () => void): () => void {
    const subject = subjectManager.getSubject(SubjectKeys.WindowResize);
    if (!subject) {
      return () => {};
    }

    const subscription = subject.subscribe(() => {
      try {
        callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in window resize callback:', error);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }

  observeIntersection(
    element: HTMLElement,
    callback: (isIntersecting: boolean) => void,
  ): () => void {
    const subject = this.getIntersectionSubject();
    if (!subject) {
      return () => {};
    }

    if (!this.intersectedElements.has(element)) {
      this.intersectedElements.add(element);
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(element);
      }
    }

    const subscription = subject
      .pipe(filter(event => event.element === element))
      .subscribe(event => {
        try {
          callback(event.isIntersecting);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error in intersection callback:', error);
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }

  getIntersectionSubject(): Subject<IntersectionEvent> | undefined {
    try {
      if (!this.intersectionSubject) {
        this.createIntersectionSubject();
      }
      return this.intersectionSubject;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get intersection subject:', error);
      return undefined;
    }
  }

  private createResizeSubject(): void {
    this.resizeSubject = new Subject<ResizeEvent>();

    this.resizeObserver = new ResizeObserver(entries => {
      try {
        requestAnimationFrame(() => {
          entries.forEach(entry => {
            if (entry.target instanceof HTMLElement) {
              this.resizeSubject?.next({
                element: entry.target,
                entry,
              });
            }
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in resize observer:', error);
      }
    });

    this.cleanupFunctions.set('resize', () => {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      this.observedElements = new WeakSet();
    });
  }

  private createMutationSubject(): void {
    this.mutationSubject = new Subject<MutationEvent>();

    this.mutationObserver = new MutationObserver(mutations => {
      try {
        const affectedElements = new Set<HTMLElement>();
        mutations.forEach(mutation => {
          if (mutation.target instanceof HTMLElement) {
            affectedElements.add(mutation.target);
          }
        });

        requestAnimationFrame(() => {
          affectedElements.forEach(element => {
            this.mutationSubject?.next({ element });
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in mutation observer:', error);
      }
    });

    this.cleanupFunctions.set('mutation', () => {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      this.mutatedElements = new WeakSet();
    });
  }

  private createIntersectionSubject(): void {
    this.intersectionSubject = new Subject<IntersectionEvent>();

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        try {
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.target instanceof HTMLElement) {
                this.intersectionSubject?.next({
                  element: entry.target,
                  isIntersecting: entry.isIntersecting,
                  entry,
                });
              }
            });
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error in intersection observer:', error);
        }
      },
      {
        threshold: 0,
        rootMargin: '0px',
      },
    );

    this.cleanupFunctions.set('intersection', () => {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      this.intersectedElements = new WeakSet();
    });
  }

  cleanup(type: 'resize' | 'mutation' | 'intersection'): void {
    const cleanup = this.cleanupFunctions.get(type);
    if (cleanup) {
      cleanup();
      this.cleanupFunctions.delete(type);
    }

    switch (type) {
      case 'resize':
        if (this.resizeSubject) {
          this.resizeSubject.complete();
          this.resizeSubject = undefined;
        }
        break;
      case 'mutation':
        if (this.mutationSubject) {
          this.mutationSubject.complete();
          this.mutationSubject = undefined;
        }
        break;
      case 'intersection':
        if (this.intersectionSubject) {
          this.intersectionSubject.complete();
          this.intersectionSubject = undefined;
        }
        break;
    }
  }

  cleanupAll(): void {
    this.cleanup('resize');
    this.cleanup('mutation');
    this.cleanup('intersection');
  }
}

export const elementObserverManager = new ElementObserverManager();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    elementObserverManager.cleanupAll();
  });
}
